import React from 'react';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, DeleteOutline, PictureAsPdf } from '@mui/icons-material';
import {
  AUTH_NUMBER_PREFIX,
  FORM_REFS
} from '../../constants/oneOffAuthorization';
import GetsBrandHeader from '../certificationAuthorization/GetsBrandHeader';

const emptyScopeRow = () => ({
  scopeOfAuthorization: '',
  limitation: ''
});

export const emptyGetsCm001aCertificate = (overrides = {}) => ({
  authorizationNo: AUTH_NUMBER_PREFIX,
  issueNo: '01',
  revNo: '00',
  dated: '',
  validFrom: '',
  validUntil: '',
  limitations: '',
  scopeRows: [emptyScopeRow()],
  name: '',
  licenseNo: '',
  category: '',
  ratings: '',
  maintenanceOrgName: '',
  aircraftModel: '',
  aircraftReg: '',
  engineModel: '',
  aircraftMsn: '',
  location: '',
  operatorName: '',
  reasonForAuth: '',
  authDate: '',
  issued: true,
  ...overrides
});

const INTRO_TEXT =
  'This ONE-OFF AUTHORIZATION is issued by GETS-MRO, a CAR 145 Approved Maintenance Organization, ' +
  'under the provisions of the MOE to authorize the named certifying staff to perform the specified ' +
  'maintenance task(s) on the identified aircraft for the validity period stated herein. ' +
  'This authorization may be withdrawn at any time if deemed necessary by the Compliance Monitoring Manager.';

const sectionSx = {
  bgcolor: '#f0d9a8',
  border: '1px solid',
  borderColor: 'text.primary',
  px: 1.5,
  py: 0.75,
  fontWeight: 700,
  textAlign: 'center',
  textTransform: 'uppercase',
  fontSize: '0.85rem',
  letterSpacing: 0.3
};

function GetsCm001aCertificateForm({
  value,
  onChange,
  readOnly = false,
  onIssue,
  onDownloadPdf,
  saving = false
}) {
  const cert = value || emptyGetsCm001aCertificate();
  const scopeRows =
    Array.isArray(cert.scopeRows) && cert.scopeRows.length ? cert.scopeRows : [emptyScopeRow()];

  const set = (patch) => onChange({ ...cert, ...patch });
  const updateScope = (index, patch) => {
    const next = scopeRows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    set({ scopeRows: next });
  };

  const fieldProps = readOnly ? { InputProps: { readOnly: true } } : {};
  const dateVal = (v) => (v ? String(v).slice(0, 10) : '');

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 1.5, sm: 2.5 }, borderColor: 'text.primary', borderWidth: 2, borderStyle: 'double' }}
    >
      <GetsBrandHeader
        title="One-Off Authorization"
        subtitle={`Form: ${FORM_REFS.certificate} | Issue: 01 Rev 00`}
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {onDownloadPdf && (
          <Button size="small" variant="outlined" startIcon={<PictureAsPdf />} onClick={onDownloadPdf}>
            Download {FORM_REFS.certificate} PDF
          </Button>
        )}
        {!readOnly && onIssue && (
          <Button size="small" variant="contained" disabled={saving} onClick={() => onIssue(true)}>
            Issue authorization
          </Button>
        )}
        {!readOnly && onIssue && (
          <Button size="small" variant="outlined" color="error" disabled={saving} onClick={() => onIssue(false)}>
            Do not issue
          </Button>
        )}
      </Stack>

      <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
        {INTRO_TEXT}
      </Typography>

      <Grid container spacing={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Grid item xs={12} md={6} sx={{ borderRight: { md: '1px solid' }, borderColor: 'divider' }}>
          <Box sx={{ ...sectionSx, border: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
            Certifying Staff
          </Box>
          <Stack spacing={1.25} sx={{ p: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              label="Name"
              value={cert.name || ''}
              onChange={(e) => set({ name: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              label="License No"
              value={cert.licenseNo || ''}
              onChange={(e) => set({ licenseNo: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              label="Category"
              value={cert.category || ''}
              onChange={(e) => set({ category: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              label="Ratings"
              value={cert.ratings || ''}
              onChange={(e) => set({ ratings: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              label="Maintenance Organization Name & Approval"
              value={cert.maintenanceOrgName || ''}
              onChange={(e) => set({ maintenanceOrgName: e.target.value })}
              {...fieldProps}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ ...sectionSx, border: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
            Aircraft
          </Box>
          <Stack spacing={1.25} sx={{ p: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              label="Model"
              value={cert.aircraftModel || ''}
              onChange={(e) => set({ aircraftModel: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              label="Reg"
              value={cert.aircraftReg || ''}
              onChange={(e) => set({ aircraftReg: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              label="Engine Model"
              value={cert.engineModel || ''}
              onChange={(e) => set({ engineModel: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              label="MSN"
              value={cert.aircraftMsn || ''}
              onChange={(e) => set({ aircraftMsn: e.target.value })}
              {...fieldProps}
            />
          </Stack>
        </Grid>
      </Grid>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase' }}>
          Other Details
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Location"
              value={cert.location || ''}
              onChange={(e) => set({ location: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.authDate)}
              onChange={(e) => set({ authDate: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Operator Name"
              value={cert.operatorName || ''}
              onChange={(e) => set({ operatorName: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Reason for Auth"
              value={cert.reasonForAuth || ''}
              onChange={(e) => set({ reasonForAuth: e.target.value })}
              {...fieldProps}
            />
          </Grid>
        </Grid>
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Scope of Authorization
        </Typography>
        {!readOnly && (
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => set({ scopeRows: [...scopeRows, emptyScopeRow()] })}
          >
            Add row
          </Button>
        )}
      </Stack>
      <Table size="small" sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f0d9a8' }}>
            <TableCell width={48}>Sl. No</TableCell>
            <TableCell>Scope of Authorization</TableCell>
            <TableCell>Limitations</TableCell>
            {!readOnly && <TableCell width={40} />}
          </TableRow>
        </TableHead>
        <TableBody>
          {scopeRows.map((row, index) => (
            <TableRow key={index}>
              <TableCell>{index + 1}</TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  variant="standard"
                  value={row.scopeOfAuthorization || ''}
                  onChange={(e) => updateScope(index, { scopeOfAuthorization: e.target.value })}
                  {...fieldProps}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  variant="standard"
                  value={row.limitation || ''}
                  onChange={(e) => updateScope(index, { limitation: e.target.value })}
                  {...fieldProps}
                />
              </TableCell>
              {!readOnly && (
                <TableCell>
                  <IconButton
                    size="small"
                    disabled={scopeRows.length <= 1}
                    onClick={() => set({ scopeRows: scopeRows.filter((_, i) => i !== index) })}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        label="Any Limitations"
        value={cert.limitations || ''}
        onChange={(e) => set({ limitations: e.target.value })}
        sx={{ mb: 2 }}
        {...fieldProps}
      />

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase' }}>
          Validity &amp; Authorization Number
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Valid From"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.validFrom)}
              onChange={(e) => set({ validFrom: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Valid Till"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.validUntil)}
              onChange={(e) => set({ validUntil: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              required
              label="Authorization Number"
              value={cert.authorizationNo || ''}
              onChange={(e) => set({ authorizationNo: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Issue"
              value={cert.issueNo || ''}
              onChange={(e) => set({ issueNo: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Rev"
              value={cert.revNo || ''}
              onChange={(e) => set({ revNo: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Dated"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.dated)}
              onChange={(e) => set({ dated: e.target.value })}
              {...fieldProps}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={1} sx={{ border: '1px solid', borderColor: 'divider', p: 1.5 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" fontWeight={700} display="block">
            Issued By — Compliance Monitoring Manager
          </Typography>
          <Typography variant="caption" color="text.secondary">
            SIGNATURE &amp; SEAL
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" fontWeight={700} display="block">
            CMM Signature
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Name / Date
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default GetsCm001aCertificateForm;
